<section class="workers-section" dir="rtl">
  <!-- Decorative background -->
  <div class="bg-decoration">
    <div class="bg-circle bg-circle--1"></div>
    <div class="bg-circle bg-circle--2"></div>
    <div class="bg-circle bg-circle--3"></div>
  </div>

  <!-- Section Header -->
  <div class="section-header">
    <div class="section-heading-main">
      <span class="section-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14.5 6a3.5 3.5 0 004.6 4.3l-8.4 8.4a2.4 2.4 0 01-3.4-3.4l8.4-8.4A3.5 3.5 0 0014.5 6z"/>
        </svg>
      </span>
      <span class="section-heading-copy">
        <span class="section-eyebrow">אנשי מקצוע מהקהילה</span>
        <h2 class="section-title" @titleEnter>{{ sectionTitle }}</h2>
        <p class="section-subtitle" @subtitleEnter>{{ sectionSubtitle }}</p>
      </span>
    </div>
    @if (totalCount > 0) {
      <button type="button" class="section-all" (click)="onShowMoreClick()">
        לכל בעלי המקצוע
      </button>
    }
  </div>

  <!-- ═══════ LOADING SKELETON ═══════ -->
  @if (loading) {
    <div class="scroll-wrapper" @fadeInOut>
      <div class="workers-grid">
        @for (s of skeletonCards; track s) {
          <div class="worker-card skeleton-card">
            <div class="skeleton-circle"></div>
            <div class="skeleton-line skeleton-line--name"></div>
            <div class="skeleton-line skeleton-line--role"></div>
            <div class="skeleton-line skeleton-line--stars"></div>
          </div>
        }
      </div>
    </div>
  }

  <!-- ═══════ ERROR STATE ═══════ -->
  @if (error && !loading) {
    <div class="error-state" @fadeInOut>
      <div class="error-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <p class="error-message">{{ error }}</p>
      <button class="btn btn--primary retry-btn" (click)="retry()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="retry-icon">
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
        </svg>
        נסה שוב
      </button>
    </div>
  }

  <!-- ═══════ WORKERS HORIZONTAL SCROLL ═══════ -->
  @if (showCards && !loading && !error) {
    <div class="scroll-container">
      <!-- Nav Arrows -->
      @if (canScrollRight) {
        <button
          class="scroll-arrow scroll-arrow--right"
          (click)="scrollBy(-340)"
          aria-label="גלול ימינה"
          >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      }
      @if (canScrollLeft) {
        <button
          class="scroll-arrow scroll-arrow--left"
          (click)="scrollBy(340)"
          aria-label="גלול שמאלה"
          >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      }
      <!-- Fade edges -->
      @if (canScrollRight) {
        <div class="scroll-fade scroll-fade--right"></div>
      }
      @if (canScrollLeft) {
        <div class="scroll-fade scroll-fade--left"></div>
      }
      <!-- Scrollable area -->
      <div
        class="scroll-wrapper"
        #scrollWrapper
        (mousedown)="onDragStart($event)"
        (scroll)="onScroll()"
        @cardsStagger
        >
        <div class="workers-grid">
          <!-- Worker Cards: hover handled via CSS class, not Angular animation -->
          @for (worker of displayWorkers; track trackByWorkerId(i, worker); let i = $index) {
            <div
              class="worker-card"
              [class.is-hovered]="hoveredCardId === worker.id"
              (mouseenter)="onCardEnter(worker.id)"
              (mouseleave)="onCardLeave(worker.id)"
              (click)="navigateToPage(worker.id)"
              role="button"
              tabindex="0"
              [attr.aria-label]="'פתיחת פרופיל: ' + worker.name"
              (keydown.enter)="navigateToPage(worker.id)"
              (keydown.space)="navigateToPage(worker.id); $event.preventDefault()"
              >
              <div class="card-glow"></div>
              @if (worker.isActive !== false) {
                <span class="availability">
                  <span aria-hidden="true"></span> זמין לפניות
                </span>
              }
              <!-- Profile Image -->
              <div class="image-container">
                <div class="image-ring">
                  <div class="image-ring-inner"></div>
                </div>
                <!-- No more [@imagePulse], CSS handles scale -->
                <div class="image-wrapper">
                  <img
                    [src]="worker.imageUrl"
                    [alt]="worker.name"
                    class="worker-image"
                    loading="lazy"
                    (error)="onImageError($event)"
                    />
                </div>
                @if (worker.isActive !== false) {
                  <div class="online-indicator">
                    <div class="online-pulse"></div>
                  </div>
                }
              </div>
              <!-- Worker Info -->
              <div class="worker-info">
                <h3 class="worker-name">{{ worker.name }}</h3>
                <span class="worker-role">{{ worker.role }}</span>
                @if (worker.city) {
                  <span class="worker-city">{{ worker.city }}</span>
                }
              </div>
              <!-- Rating: uses precomputed starArray and starDelays -->
              <div class="rating-container">
                <div class="stars">
                  @for (star of worker.starArray; track star; let j = $index) {
                    <span
                      class="star"
                      [style.animation-delay]="worker.starDelays[j]"
                                    [ngClass]="{
                                        'star--full': star.type === 'full',
                                        'star--half': star.type === 'half',
                                        'star--empty': star.type === 'empty'
                                    }"
                      >
                      @if (star.type === 'full') {
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      }
                      @if (star.type === 'half') {
                        <svg viewBox="0 0 24 24">
                          <defs>
                            <linearGradient [attr.id]="'halfGrad' + i + j">
                              <stop offset="50%" stop-color="currentColor" />
                              <stop offset="50%" stop-color="#E0E0E0" />
                            </linearGradient>
                          </defs>
                          <path [attr.fill]="'url(#halfGrad' + i + j + ')'"
                            d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      }
                      @if (star.type === 'empty') {
                        <svg viewBox="0 0 24 24" fill="#E0E0E0">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      }
                    </span>
                  }
                </div>
                <span class="rating-number">{{ worker.rating }}</span>
              </div>
              <span class="profile-cta">לצפייה בפרופיל <span aria-hidden="true">←</span></span>
            </div>
          }
          <!-- Show More Card -->
          @if (showMoreCard) {
            <div
              class="worker-card show-more-card"
              (click)="onShowMoreClick()"
              role="button"
              tabindex="0"
              aria-label="הצגת כל אנשי המקצוע"
              (keydown.enter)="onShowMoreClick()"
              (keydown.space)="onShowMoreClick(); $event.preventDefault()"
              >
              <div class="show-more-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="16" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
              </div>
              <span class="show-more-text">הצג הכל</span>
              <span class="show-more-count">{{ totalCount }} בעלי מקצוע</span>
              <div class="show-more-arrow">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Total count -->
  @if (!loading && !error && totalCount > 0) {
    <div
      class="total-count"
      @fadeInOut
      >
      סה״כ {{ totalCount }} בעלי מקצוע
    </div>
  }
</section>
